import _ from 'lodash';
import { useContext, useEffect, useMemo } from 'react';
import { INVOICE_STATUS } from '../constants/enums';
import AuthContext from '../contexts/AuthContext';
import InvoiceDataContext from '../contexts/InvoiceDataContext';
import MerchantContext from '../contexts/MerchantContext';
import { useTRPC } from '../trpc/client';
import { useQuery } from '@tanstack/react-query';
import type { Invoice } from '../types';

export const useInvoices = () => {
  const trpc = useTRPC();
  const { user, documentType, documentNumber, setDocumentNumber } = useContext(AuthContext);
  const { merchant } = useContext(MerchantContext);
  const { setInvoiceData } = useContext(InvoiceDataContext);

  const queryOptions = trpc.invoices.list.queryOptions({
    rentalsMerchantId: merchant?.id ?? '',
    customerIdentifier: user?.trim() ?? '',
    customerDocumentType: documentType,
  });

  const invoiceRequest = useQuery({
    ...queryOptions,
    enabled: !!user && !!merchant?.id,
  });

  const resolvedDocumentNumber = invoiceRequest.data?.customer?.documentNumber;

  useEffect(() => {
    if (resolvedDocumentNumber && resolvedDocumentNumber !== documentNumber) setDocumentNumber(resolvedDocumentNumber);
  }, [resolvedDocumentNumber, documentNumber, setDocumentNumber]);

  useEffect(() => {
    if (invoiceRequest.data) {
      setInvoiceData({
        isBlockedForCardPayments: invoiceRequest.data.isBlockedForCardPayments,
        cardFeePercentage: invoiceRequest.data.cardFeePercentage,
        creditsBalance: invoiceRequest.data.customer?.creditsBalance ?? 0,
      });
    }
  }, [invoiceRequest.data, setInvoiceData]);

  const readyInvoices = useMemo(() => {
    const filtered = invoiceRequest.data?.invoices.filter((ele: Invoice) => ele.status === INVOICE_STATUS.READY) || [];
    return _.sortBy(filtered, (ele: Invoice) => new Date(ele.dueDate).getTime());
  }, [invoiceRequest.data?.invoices]);

  const paidInvoices = useMemo(() => {
    const filtered = invoiceRequest.data?.invoices.filter((ele: Invoice) => ele.status === INVOICE_STATUS.PAID) || [];
    return _.sortBy(filtered, (ele: Invoice) => new Date(ele.paymentDate!).getTime()).reverse();
  }, [invoiceRequest.data?.invoices]);

  return {
    readyInvoices,
    paidInvoices,
    isLoading: invoiceRequest.isPending,
    cardFeePercentage: invoiceRequest.data?.cardFeePercentage,
    isBlockedForCardPayments: invoiceRequest.data?.isBlockedForCardPayments,
    creditsBalance: invoiceRequest.data?.customer?.creditsBalance ?? 0,
    refetch: invoiceRequest.refetch,
  };
};
